import { useCallback, useRef } from "react";
import type { UUID } from "../../../__definitions__/iconfinder.changeengine.com-definitions";
import { convertToLettersOnly } from "../../../utils/convert-to-letters-only";
import { useDialog } from "../../../utils/hooks/use-dialog";
import type { DisplayConditionData } from "../../unlayer-email-content/components/display-condition-dialog";
import type { DisplayConditionReturn } from "../../unlayer-email-content/hooks/use-display-condition";

interface UseFollowUpDisplayConditionOptions {
  getDynamicSegmentIdMap: () => Record<string, string> | null;
  setDynamicSegmentIdMap: (map: Record<string, string>) => void;
}

export function useFollowUpDisplayCondition({
  getDynamicSegmentIdMap,
  setDynamicSegmentIdMap,
}: UseFollowUpDisplayConditionOptions): DisplayConditionReturn {
  const { open, handleClose, handleOpen, data: selectedId } = useDialog<UUID | undefined>();
  const doneCallbackRef = useRef<((data: DisplayConditionData) => void) | null>(null);

  const onDisplayConditionRequest = useCallback(
    (dcData: DisplayConditionData, done: (data: DisplayConditionData) => void) => {
      doneCallbackRef.current = done;
      handleOpen(dcData.id);
    },
    [handleOpen],
  );

  const onConfirm = useCallback(
    (conditionData: DisplayConditionData) => {
      if (doneCallbackRef.current) {
        const currentConditions = getDynamicSegmentIdMap();
        setDynamicSegmentIdMap({
          ...currentConditions,
          [conditionData.id]: convertToLettersOnly(conditionData.id),
        });
        doneCallbackRef.current(conditionData);
        doneCallbackRef.current = null;
      }
      handleClose();
    },
    [handleClose, getDynamicSegmentIdMap, setDynamicSegmentIdMap],
  );

  const onClose = useCallback(() => {
    handleClose();
    doneCallbackRef.current = null;
  }, [handleClose]);

  return { isOpen: open, onDisplayConditionRequest, onConfirm, onClose, selectedId };
}
